import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { getLeaves, createLeave, getEmployees, modifyLeave, createBackdatedLeave, deleteLeave, getLeaveAuditLog } from '../api';
import api from '../api';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  FileText, Plus, Check, X, Calendar, Clock, User, Users, 
  Building2, CheckCircle, XCircle, AlertCircle, ArrowRight,
  ChevronRight, Eye, RefreshCw, Edit, CalendarPlus, History, Trash2, ScrollText,
  ChevronLeft, CalendarDays, LayoutList
} from 'lucide-react';
import { toast } from 'sonner';
import LeaveQuotaCard from '../components/LeaveQuotaCard';
import LeaveSplitConfirmDialog from '../components/LeaveSplitConfirmDialog';
import LwpForecastCard from '../components/LwpForecastCard';

// Team Leave Calendar Component
const TeamLeaveCalendar = ({ teamLeaves, calendarMonth, setCalendarMonth, conflictThreshold = 2 }) => {
  const CONFLICT_THRESHOLD = conflictThreshold; // Configurable per department
  
  const leaveTypeColors = {
    casual: { bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-300' },
    sick: { bg: 'bg-rose-100', text: 'text-rose-800', border: 'border-rose-300' },
    annual: { bg: 'bg-purple-100', text: 'text-purple-800', border: 'border-purple-300' },
    privilege: { bg: 'bg-purple-100', text: 'text-purple-800', border: 'border-purple-300' },
    special: { bg: 'bg-fuchsia-100', text: 'text-fuchsia-800', border: 'border-fuchsia-300' },
    maternity: { bg: 'bg-pink-100', text: 'text-pink-800', border: 'border-pink-300' },
    paternity: { bg: 'bg-cyan-100', text: 'text-cyan-800', border: 'border-cyan-300' },
    bereavement: { bg: 'bg-gray-100', text: 'text-gray-800', border: 'border-gray-300' },
    unpaid: { bg: 'bg-amber-100', text: 'text-amber-800', border: 'border-amber-300' },
    compensatory: { bg: 'bg-emerald-100', text: 'text-emerald-800', border: 'border-emerald-300' }
  };

  const statusColors = {
    approved: 'border-l-emerald-500',
    pending: 'border-l-amber-500',
    rejected: 'border-l-rose-500'
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    return new Date(year, month, 1).getDay();
  };

  const formatDate = (date) => {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  };

  const formatDisplayDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const isDateInRange = (date, startDate, endDate) => {
    const d = new Date(date);
    const start = new Date(startDate);
    const end = new Date(endDate);
    return d >= start && d <= end;
  };

  const getLeavesForDate = (date) => {
    const dateStr = formatDate(date);
    return teamLeaves.filter(leave => {
      return isDateInRange(dateStr, leave.start_date, leave.end_date);
    });
  };

  const getApprovedLeavesForDate = (date) => {
    return getLeavesForDate(date).filter(leave => leave.status === 'approved');
  };

  const isConflictDay = (date) => {
    const approvedLeaves = getApprovedLeavesForDate(date);
    return approvedLeaves.length >= CONFLICT_THRESHOLD;
  };

  // Find all conflict days in the current month
  const getConflictDays = () => {
    const conflicts = [];
    const daysInMonth = getDaysInMonth(calendarMonth);
    
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), day);
      const approvedLeaves = getApprovedLeavesForDate(date);
      
      if (approvedLeaves.length >= CONFLICT_THRESHOLD) {
        conflicts.push({
          date: formatDate(date),
          displayDate: formatDisplayDate(formatDate(date)),
          count: approvedLeaves.length,
          employees: approvedLeaves.map(l => ({
            name: l.employee_name,
            type: l.leave_type
          }))
        });
      }
    }
    
    return conflicts;
  };

  const prevMonth = () => {
    setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1, 1));
  };

  const daysInMonth = getDaysInMonth(calendarMonth);
  const firstDayOfMonth = getFirstDayOfMonth(calendarMonth);
  const monthName = calendarMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const today = new Date();
  const conflictDays = getConflictDays();

  // Build calendar grid
  const calendarDays = [];
  
  // Empty cells before first day
  for (let i = 0; i < firstDayOfMonth; i++) {
    calendarDays.push(null);
  }
  
  // Days of the month
  for (let day = 1; day <= daysInMonth; day++) {
    calendarDays.push(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), day));
  }

  return (
    <div className="space-y-4">
      {/* Conflict Alert Banner */}
      {conflictDays.length > 0 && (
        <div className="bg-gradient-to-r from-rose-50 to-orange-50 border border-rose-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-rose-100 rounded-full">
              <AlertCircle className="text-rose-600" size={20} />
            </div>
            <div className="flex-1">
              <h4 className="font-semibold text-rose-800 flex items-center gap-2">
                Leave Conflicts Detected
                <Badge className="bg-rose-500 text-white">{conflictDays.length} day(s)</Badge>
              </h4>
              <p className="text-sm text-rose-600 mt-1">
                Multiple team members ({CONFLICT_THRESHOLD}+) are on approved leave on the following days:
              </p>
              <div className="mt-3 space-y-2">
                {conflictDays.map((conflict, idx) => (
                  <div key={idx} className="flex items-center gap-4 bg-white/60 rounded-lg p-2">
                    <div className="flex items-center gap-2 min-w-[140px]">
                      <Calendar className="text-rose-500" size={14} />
                      <span className="font-medium text-sm text-slate-800">{conflict.displayDate}</span>
                    </div>
                    <Badge variant="outline" className="bg-rose-100 text-rose-700 border-rose-300">
                      {conflict.count} on leave
                    </Badge>
                    <div className="flex flex-wrap gap-1">
                      {conflict.employees.map((emp, i) => (
                        <span key={i} className="text-xs bg-white px-2 py-0.5 rounded border border-slate-200">
                          {emp.name?.split(' ')[0]} ({emp.type})
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Calendar Header */}
      <div className="flex items-center justify-between">
        <Button variant="outline" size="sm" onClick={prevMonth}>
          <ChevronLeft size={16} />
        </Button>
        <h3 className="text-lg font-semibold text-slate-800">{monthName}</h3>
        <Button variant="outline" size="sm" onClick={nextMonth}>
          <ChevronRight size={16} />
        </Button>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-2 text-xs">
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded bg-emerald-500"></span> Approved
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded bg-amber-500"></span> Pending
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded bg-rose-500"></span> Rejected
        </span>
        <span className="mx-2">|</span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded bg-gradient-to-r from-rose-400 to-orange-400"></span> Conflict ({CONFLICT_THRESHOLD}+ on leave)
        </span>
        <span className="mx-2">|</span>
        {Object.entries(leaveTypeColors).slice(0, 4).map(([type, colors]) => (
          <span key={type} className="flex items-center gap-1">
            <span className={`w-3 h-3 rounded ${colors.bg}`}></span> 
            <span className="capitalize">{type}</span>
          </span>
        ))}
      </div>

      {/* Calendar Grid */}
      <div className="border rounded-lg overflow-hidden">
        {/* Week Days Header */}
        <div className="grid grid-cols-7 bg-slate-50">
          {weekDays.map(day => (
            <div key={day} className="py-2 text-center text-sm font-medium text-slate-600 border-b">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Days */}
        <div className="grid grid-cols-7">
          {calendarDays.map((date, index) => {
            if (!date) {
              return <div key={`empty-${index}`} className="min-h-[100px] bg-slate-50 border-b border-r" />;
            }

            const leaves = getLeavesForDate(date);
            const isToday = date.toDateString() === today.toDateString();
            const isWeekend = date.getDay() === 0 || date.getDay() === 6;
            const hasConflict = isConflictDay(date);
            const approvedCount = getApprovedLeavesForDate(date).length;

            return (
              <div 
                key={date.toISOString()} 
                className={`min-h-[100px] border-b border-r p-1 relative
                  ${isWeekend ? 'bg-slate-50' : 'bg-white'} 
                  ${isToday ? 'ring-2 ring-indigo-500 ring-inset' : ''} 
                  ${hasConflict ? 'bg-gradient-to-br from-rose-50 to-orange-50' : ''}`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className={`text-sm font-medium ${isToday ? 'text-indigo-600' : 'text-slate-700'}`}>
                    {date.getDate()}
                  </span>
                  {hasConflict && (
                    <span className="flex items-center gap-0.5 text-[10px] font-medium text-rose-600 bg-rose-100 px-1 rounded">
                      <AlertCircle size={10} />
                      {approvedCount}
                    </span>
                  )}
                </div>
                <div className="space-y-1 max-h-[70px] overflow-y-auto">
                  {leaves.slice(0, 3).map((leave, idx) => {
                    const typeColor = leaveTypeColors[leave.leave_type] || leaveTypeColors.casual;
                    const statusBorder = statusColors[leave.status] || statusColors.pending;
                    
                    return (
                      <div 
                        key={`${leave.id}-${idx}`}
                        className={`text-[10px] px-1 py-0.5 rounded truncate border-l-2 ${typeColor.bg} ${typeColor.text} ${statusBorder}`}
                        title={`${leave.employee_name} - ${leave.leave_type} (${leave.status})`}
                      >
                        {leave.employee_name?.split(' ')[0]}
                      </div>
                    );
                  })}
                  {leaves.length > 3 && (
                    <div className="text-[10px] text-slate-500 px-1">
                      +{leaves.length - 3} more
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Summary for current month */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-4">
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
          <p className="text-xs text-emerald-600">Approved Leaves</p>
          <p className="text-xl font-bold text-emerald-800">
            {teamLeaves.filter(l => l.status === 'approved' && 
              (l.start_date?.startsWith(`${calendarMonth.getFullYear()}-${String(calendarMonth.getMonth() + 1).padStart(2, '0')}`) ||
               l.end_date?.startsWith(`${calendarMonth.getFullYear()}-${String(calendarMonth.getMonth() + 1).padStart(2, '0')}`))
            ).length}
          </p>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
          <p className="text-xs text-amber-600">Pending Leaves</p>
          <p className="text-xl font-bold text-amber-800">
            {teamLeaves.filter(l => l.status === 'pending' && 
              (l.start_date?.startsWith(`${calendarMonth.getFullYear()}-${String(calendarMonth.getMonth() + 1).padStart(2, '0')}`) ||
               l.end_date?.startsWith(`${calendarMonth.getFullYear()}-${String(calendarMonth.getMonth() + 1).padStart(2, '0')}`))
            ).length}
          </p>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <p className="text-xs text-blue-600">Total Days</p>
          <p className="text-xl font-bold text-blue-800">
            {teamLeaves.filter(l => l.status === 'approved' && 
              (l.start_date?.startsWith(`${calendarMonth.getFullYear()}-${String(calendarMonth.getMonth() + 1).padStart(2, '0')}`) ||
               l.end_date?.startsWith(`${calendarMonth.getFullYear()}-${String(calendarMonth.getMonth() + 1).padStart(2, '0')}`))
            ).reduce((sum, l) => sum + (l.total_days || 1), 0)}
          </p>
        </div>
        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3">
          <p className="text-xs text-indigo-600">Team Members on Leave</p>
          <p className="text-xl font-bold text-indigo-800">
            {new Set(teamLeaves.filter(l => l.status === 'approved' && 
              (l.start_date?.startsWith(`${calendarMonth.getFullYear()}-${String(calendarMonth.getMonth() + 1).padStart(2, '0')}`) ||
               l.end_date?.startsWith(`${calendarMonth.getFullYear()}-${String(calendarMonth.getMonth() + 1).padStart(2, '0')}`))
            ).map(l => l.employee_id)).size}
          </p>
        </div>
        <div className={`rounded-lg p-3 ${conflictDays.length > 0 ? 'bg-rose-50 border border-rose-200' : 'bg-slate-50 border border-slate-200'}`}>
          <p className={`text-xs ${conflictDays.length > 0 ? 'text-rose-600' : 'text-slate-600'}`}>Conflict Days</p>
          <p className={`text-xl font-bold ${conflictDays.length > 0 ? 'text-rose-800' : 'text-slate-800'}`}>
            {conflictDays.length}
          </p>
          {conflictDays.length > 0 && (
            <p className="text-[10px] text-rose-500 mt-1">{CONFLICT_THRESHOLD}+ members same day</p>
          )}
        </div>
      </div>
    </div>
  );
};

const LeaveManagement = ({ user, setUser }) => {
  const [leaves, setLeaves] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [currentEmployee, setCurrentEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [splitPreview, setSplitPreview] = useState(null);
  const [splitDialogOpen, setSplitDialogOpen] = useState(false);
  const [splitBusy, setSplitBusy] = useState(false);
  const [workflowDialogOpen, setWorkflowDialogOpen] = useState(false);
  const [selectedLeave, setSelectedLeave] = useState(null);
  const [approvalRemarks, setApprovalRemarks] = useState('');
  const [pendingCounts, setPendingCounts] = useState({ manager: 0, hr: 0, admin: 0, total: 0 });
  const [activeTab, setActiveTab] = useState('all');
  const [leaveBalance, setLeaveBalance] = useState(null);
  
  // Modify leave dialog state
  const [modifyDialogOpen, setModifyDialogOpen] = useState(false);
  const [leaveToModify, setLeaveToModify] = useState(null);
  const [modifyData, setModifyData] = useState({
    start_date: '',
    end_date: '',
    leave_type: '',
    reason: '',
    is_half_day: false,
    modification_reason: ''
  });
  
  // Backdated leave dialog state
  const [backdatedDialogOpen, setBackdatedDialogOpen] = useState(false);
  const [backdatedData, setBackdatedData] = useState({
    employee_id: '',
    leave_type: 'casual',
    start_date: '',
    end_date: '',
    reason: '',
    is_half_day: false,
    auto_approve: true
  });
  const [employeeSearchQuery, setEmployeeSearchQuery] = useState('');
  
  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [leaveToDelete, setLeaveToDelete] = useState(null);
  
  // Audit log state
  const [auditLogDialogOpen, setAuditLogDialogOpen] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  
  // Conflict threshold state (configurable per department)
  const [conflictThreshold, setConflictThreshold] = useState(2);
  
  // Team leaves state (for managers)
  const [teamLeaves, setTeamLeaves] = useState([]);
  const [teamPendingLeaves, setTeamPendingLeaves] = useState([]);
  const [teamSummary, setTeamSummary] = useState(null);
  const [teamBalances, setTeamBalances] = useState(null);
  const [hasTeam, setHasTeam] = useState(false);
  const [loadingTeam, setLoadingTeam] = useState(false);
  const [teamViewMode, setTeamViewMode] = useState('list'); // 'list' or 'calendar'
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  
  const [formData, setFormData] = useState({
    employee_id: '',
    leave_type: 'casual',
    start_date: '',
    end_date: '',
    reason: '',
    is_half_day: false,
    half_day_type: ''
  });

  useEffect(() => {
    fetchData();
    fetchTeamLeaves();
  }, []);

  const fetchData = async () => {
    try {
      const [leavesRes, empRes] = await Promise.all([
        getLeaves(),
        getEmployees()
      ]);
      
      const allEmployees = empRes.data;
      setEmployees(allEmployees);
      
      if (user?.role === 'employee') {
        const myEmployee = allEmployees.find(e => e.user_id === user?.id);
        setCurrentEmployee(myEmployee);
        
        const myLeaves = leavesRes.data.filter(l => l.employee_id === myEmployee?.id);
        setLeaves(myLeaves);
        
        if (myEmployee) {
          setFormData(prev => ({ ...prev, employee_id: myEmployee.id }));
          // Fetch leave balance
          try {
            const balanceRes = await api.get(`/leaves/balance/${myEmployee.id}`);
            setLeaveBalance(balanceRes.data);
          } catch { /* legacy endpoint optional */ }
        }
      } else {
        setLeaves(leavesRes.data);
        // Fetch pending counts for admin/hr
        try {
          const countsRes = await api.get('/leaves/pending/count');
          setPendingCounts(countsRes.data);
        } catch { /* pending-count is optional */ }
      }
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  // Fetch team leaves for managers
  const fetchTeamLeaves = async () => {
    try {
      setLoadingTeam(true);
      const [pendingRes, summaryRes, allTeamRes, thresholdRes, balancesRes] = await Promise.allSettled([
        api.get('/leaves/team/pending'),
        api.get('/leaves/team/summary'),
        api.get('/leaves/team/all'),
        api.get('/leave-conflict-threshold/for-team'),
        api.get('/leaves/team/balances')
      ]);
      
      if (pendingRes.status === 'fulfilled') {
        setTeamPendingLeaves(pendingRes.value.data.leaves || []);
        setTeamSummary(pendingRes.value.data);
        setHasTeam(pendingRes.value.data.team_size > 0);
      }
      if (allTeamRes.status === 'fulfilled') {
        setTeamLeaves(allTeamRes.value.data || []);
      }
      if (thresholdRes.status === 'fulfilled' && thresholdRes.value.data?.threshold) {
        setConflictThreshold(thresholdRes.value.data.threshold);
      }
      if (summaryRes.status === 'fulfilled' && summaryRes.value.data) {
        setTeamSummary(prev => ({ ...prev, ...summaryRes.value.data }));
      }
      if (balancesRes.status === 'fulfilled') {
        setTeamBalances(balancesRes.value.data);
      }
    } catch (error) {
      setHasTeam(false);
    } finally {
      setLoadingTeam(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await submitLeave(formData);
  };

  const submitLeave = async (payload) => {
    try {
      const response = await createLeave(payload);
      if (response.data.split_applied) {
        toast.warning(response.data.message || 'Leave submitted with auto-split to LWP', { duration: 6000 });
      } else if (response.data.auto_unpaid) {
        toast.warning(response.data.message || 'Leave converted to unpaid', { duration: 6000 });
      } else {
        toast.success(response.data.message || 'Leave request submitted successfully');
      }
      setDialogOpen(false);
      setSplitPreview(null);
      setSplitDialogOpen(false);
      fetchData();
      setFormData({
        employee_id: user?.role === 'employee' && currentEmployee ? currentEmployee.id : '',
        leave_type: 'casual',
        start_date: '',
        end_date: '',
        reason: '',
        is_half_day: false,
        half_day_type: ''
      });
    } catch (error) {
      // 409 = needs split confirmation
      const data = error.response?.data;
      if (error.response?.status === 409 && data?.needs_split) {
        setSplitPreview({
          ...data,
          requested_type: data.requested_type || payload.leave_type,
        });
        setSplitDialogOpen(true);
        return;
      }
      toast.error(data?.detail || 'Failed to submit leave request');
    }
  };

  const handleConfirmSplit = async () => {
    setSplitBusy(true);
    try {
      await submitLeave({ ...formData, confirm_split: true });
    } finally {
      setSplitBusy(false);
    }
  };

  const handleApproval = async (leaveId, action) => {
    try {
      const response = await api.put(`/leaves/${leaveId}/approve`, {
        action: action,
        remarks: approvalRemarks
      });
      toast.success(response.data.message);
      setApprovalRemarks('');
      setWorkflowDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Action failed');
    }
  };

  // Open modify dialog
  const handleModifyClick = (leave) => {
    setLeaveToModify(leave);
    setModifyData({
      start_date: leave.start_date || '',
      end_date: leave.end_date || '',
      leave_type: leave.leave_type || '',
      reason: leave.reason || '',
      is_half_day: leave.is_half_day || false,
      modification_reason: ''
    });
    setModifyDialogOpen(true);
  };

  // Submit modified leave
  const handleModifySubmit = async () => {
    if (!modifyData.modification_reason.trim()) {
      toast.error('Please provide a reason for modification');
      return;
    }
    
    try {
      const response = await modifyLeave(leaveToModify.id, modifyData);
      toast.success(response.data.message || 'Leave modified successfully');
      setModifyDialogOpen(false);
      setLeaveToModify(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to modify leave');
    }
  };

  // Submit backdated leave
  const handleBackdatedSubmit = async () => {
    if (!backdatedData.employee_id) {
      toast.error('Please select an employee');
      return;
    }
    if (!backdatedData.start_date || !backdatedData.end_date) {
      toast.error('Please select start and end dates');
      return;
    }
    
    try {
      const response = await createBackdatedLeave(backdatedData);
      toast.success(response.data.message || 'Backdated leave created successfully');
      setBackdatedDialogOpen(false);
      setBackdatedData({
        employee_id: '',
        leave_type: 'casual',
        start_date: '',
        end_date: '',
        reason: '',
        is_half_day: false,
        auto_approve: true
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create backdated leave');
    }
  };

  // Get yesterday's date for backdated leave max date
  const getYesterdayDate = () => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return yesterday.toISOString().split('T')[0];
  };

  // Delete leave handler
  const handleDeleteClick = (leave) => {
    setLeaveToDelete(leave);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!leaveToDelete) return;
    try {
      await deleteLeave(leaveToDelete.id);
      toast.success('Leave deleted successfully');
      setDeleteDialogOpen(false);
      setLeaveToDelete(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete leave');
    }
  };

  // Audit log handler
  const fetchAuditLogs = async () => {
    try {
      setLoadingLogs(true);
      const response = await getLeaveAuditLog({ limit: 100 });
      setAuditLogs(response.data.logs || []);
    } catch (error) {
      toast.error('Failed to load audit logs');
    } finally {
      setLoadingLogs(false);
    }
  };

  const openAuditLog = () => {
    setAuditLogDialogOpen(true);
    fetchAuditLogs();
  };

  const getActionBadge = (action) => {
    const actionStyles = {
      created: { bg: 'bg-blue-100', text: 'text-blue-700' },
      approved: { bg: 'bg-green-100', text: 'text-green-700' },
      rejected: { bg: 'bg-red-100', text: 'text-red-700' },
      modified: { bg: 'bg-amber-100', text: 'text-amber-700' },
      deleted: { bg: 'bg-red-100', text: 'text-red-700' },
      backdated: { bg: 'bg-purple-100', text: 'text-purple-700' },
      cancelled: { bg: 'bg-slate-100', text: 'text-slate-700' }
    };
    const style = actionStyles[action] || { bg: 'bg-slate-100', text: 'text-slate-700' };
    return <Badge className={`${style.bg} ${style.text} capitalize`}>{action}</Badge>;
  };

  // Filter employees for search
  const filteredEmployees = employees.filter(emp => {
    if (!employeeSearchQuery) return true;
    const searchLower = employeeSearchQuery.toLowerCase();
    return (
      emp.name?.toLowerCase().includes(searchLower) ||
      emp.employee_id?.toLowerCase().includes(searchLower)
    );
  });

  const viewWorkflow = (leave) => {
    setSelectedLeave(leave);
    setWorkflowDialogOpen(true);
  };

  const getEmployeeName = (empId) => {
    const emp = employees.find(e => e.id === empId);
    return emp?.name || emp?.employee_id || 'N/A';
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge variant="outline" className="bg-amber-50 text-amber-600 border-amber-200"><Clock className="h-3 w-3 mr-1" />Pending</Badge>;
      case 'approved':
        return <Badge variant="outline" className="bg-emerald-50 text-emerald-600 border-emerald-200"><CheckCircle className="h-3 w-3 mr-1" />Approved</Badge>;
      case 'rejected':
        return <Badge variant="outline" className="bg-rose-50 text-rose-600 border-rose-200"><XCircle className="h-3 w-3 mr-1" />Rejected</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getApprovalLevelBadge = (level) => {
    const colors = {
      manager: 'bg-blue-100 text-blue-700',
      hr: 'bg-purple-100 text-purple-700',
      admin: 'bg-indigo-100 text-indigo-700'
    };
    return <Badge className={colors[level] || 'bg-gray-100'}>{level?.toUpperCase()}</Badge>;
  };

  const getWorkflowStepIcon = (status) => {
    switch (status) {
      case 'approved':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'rejected':
        return <XCircle className="h-5 w-5 text-red-500" />;
      case 'pending':
        return <Clock className="h-5 w-5 text-amber-500" />;
      default:
        return <AlertCircle className="h-5 w-5 text-gray-400" />;
    }
  };

  const canManageLeaves = user?.role === 'admin' || user?.role === 'hr_manager';
  
  const canApprove = (leave) => {
    if (leave.status !== 'pending') return false;
    const level = leave.current_approval_level;
    if (user?.role === 'admin') return true;
    if (user?.role === 'hr_manager' && (level === 'manager' || level === 'hr')) return true;
    // Reporting managers (any role) can approve at manager level
    // Team pending leaves are already filtered for the current user's subordinates
    if (level === 'manager' && hasTeam) {
      const isTeamMemberLeave = teamPendingLeaves.some(tl => tl.id === leave.id);
      if (isTeamMemberLeave) return true;
    }
    return false;
  };

  const filteredLeaves = leaves.filter(leave => {
    if (activeTab === 'team') return false; // Team tab handled separately
    if (activeTab === 'all') return true;
    if (activeTab === 'pending') return leave.status === 'pending';
    if (activeTab === 'approved') return leave.status === 'approved';
    if (activeTab === 'rejected') return leave.status === 'rejected';
    return true;
  });

  if (loading) {
    return (
      <Layout user={user} setUser={setUser}>
        <div className="flex items-center justify-center h-96">
          <RefreshCw className="h-8 w-8 animate-spin text-indigo-600" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} setUser={setUser}>
      <div className="space-y-6" data-testid="leave-management">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900" data-testid="page-title">
              {user?.role === 'employee' ? 'My Leaves' : 'Leave Management'}
            </h1>
            <p className="text-gray-500 mt-1">
              {user?.role === 'employee' 
                ? 'Apply for leave and track your requests' 
                : 'Multi-level leave approval workflow'}
            </p>
          </div>
          <div className="flex gap-2">
            {/* Audit Log Button - Admin only */}
            {user?.role === 'admin' && (
              <Button 
                variant="outline" 
                onClick={openAuditLog}
                className="border-slate-300 text-slate-600 hover:bg-slate-50"
                data-testid="audit-log-btn"
              >
                <ScrollText className="h-4 w-4 mr-2" />
                Audit Log
              </Button>
            )}
            {/* Backdated Leave Button - HR Manager and Admin only */}
            {(user?.role === 'admin' || user?.role === 'hr_manager') && (
              <Button 
                variant="outline" 
                onClick={() => setBackdatedDialogOpen(true)}
                className="border-amber-300 text-amber-600 hover:bg-amber-50"
                data-testid="backdated-leave-btn"
              >
                <CalendarPlus className="h-4 w-4 mr-2" />
                Backdated Leave
              </Button>
            )}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-indigo-600 hover:bg-indigo-700" data-testid="apply-leave-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Apply for Leave
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Apply for Leave</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  {canManageLeaves && (
                    <div>
                      <Label>Employee *</Label>
                    <Select
                      value={formData.employee_id}
                      onValueChange={(value) => setFormData({ ...formData, employee_id: value })}
                    >
                      <SelectTrigger><SelectValue placeholder="Select employee" /></SelectTrigger>
                      <SelectContent>
                        {employees.map(emp => (
                          <SelectItem key={emp.id} value={emp.id}>{emp.name || emp.employee_id}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div>
                  <Label>Leave Type *</Label>
                  <Select
                    value={formData.leave_type}
                    onValueChange={(value) => setFormData({ ...formData, leave_type: value })}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sick">Sick Leave (SL)</SelectItem>
                      <SelectItem value="casual">Casual Leave (CL)</SelectItem>
                      <SelectItem value="annual">Annual / Privilege Leave (PL)</SelectItem>
                      <SelectItem value="special">Special Leave (Birthday / Anniversary)</SelectItem>
                      <SelectItem value="unpaid">Leave Without Pay (LWP)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Start Date *</Label>
                    <Input
                      type="date"
                      value={formData.start_date}
                      onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label>End Date *</Label>
                    <Input
                      type="date"
                      value={formData.end_date}
                      onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                      required
                    />
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.is_half_day}
                      onChange={(e) => setFormData({ ...formData, is_half_day: e.target.checked })}
                      className="rounded"
                    />
                    <span className="text-sm">Half Day</span>
                  </label>
                  {formData.is_half_day && (
                    <Select
                      value={formData.half_day_type}
                      onValueChange={(value) => setFormData({ ...formData, half_day_type: value })}
                    >
                      <SelectTrigger className="w-32"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="first_half">First Half</SelectItem>
                        <SelectItem value="second_half">Second Half</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                </div>
                <div>
                  <Label>Reason *</Label>
                  <Textarea
                    value={formData.reason}
                    onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                    placeholder="Provide reason for leave"
                    required
                  />
                </div>
                <DialogFooter>
                  <Button type="submit">Submit Request</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {/* Stats Cards for Admin/HR */}
        {canManageLeaves && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Pending (Manager)</p>
                    <p className="text-2xl font-bold text-blue-600">{pendingCounts.manager}</p>
                  </div>
                  <User className="h-8 w-8 text-blue-200" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Pending (HR)</p>
                    <p className="text-2xl font-bold text-purple-600">{pendingCounts.hr}</p>
                  </div>
                  <Users className="h-8 w-8 text-purple-200" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Pending (Admin)</p>
                    <p className="text-2xl font-bold text-indigo-600">{pendingCounts.admin}</p>
                  </div>
                  <Building2 className="h-8 w-8 text-indigo-200" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Total Pending</p>
                    <p className="text-2xl font-bold text-amber-600">{pendingCounts.total}</p>
                  </div>
                  <Clock className="h-8 w-8 text-amber-200" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Leave Balance for Employees (legacy — superseded by LeaveQuotaCard) */}
        {false && user?.role === 'employee' && leaveBalance && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Leave Balance ({leaveBalance.year})</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {Object.entries(leaveBalance.balance).map(([type, data]) => (
                  <div key={type} className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-500 capitalize">{type.replace('_', ' ')}</p>
                    <p className="text-xl font-bold">
                      {typeof data.balance === 'number' ? data.balance : data.balance}
                    </p>
                    <p className="text-xs text-gray-400">Used: {data.used} / Quota: {data.quota || '∞'}</p>
                  </div>
                ))}
              </div>
              {leaveBalance.accrual && (
                <div className="mt-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                  <p className="text-sm font-medium text-orange-800">
                    Monthly Accrual: {leaveBalance.accrual.monthly_rate} leaves/month
                    {leaveBalance.accrual.in_probation && (
                      <span className="ml-2 text-red-600">(In probation — no leaves accrued yet)</span>
                    )}
                  </p>
                  <p className="text-xs text-orange-600 mt-1">
                    Accrued so far: <strong>{leaveBalance.accrual.accrued_so_far}</strong> of {leaveBalance.accrual.total_annual} annual
                    {' | '}Used: <strong>{leaveBalance.accrual.total_used_paid}</strong>
                    {' | '}Available: <strong>{leaveBalance.accrual.remaining_accrued}</strong>
                    {leaveBalance.accrual.remaining_accrued <= 0 && ' (exceeded — further leaves will be unpaid)'}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Personal quota card — show only for employees viewing their own leaves */}
        {currentEmployee?.id && (
          <div className="mb-4">
            <LeaveQuotaCard employeeId={currentEmployee.id} />
          </div>
        )}

        {/* HR/Admin/Manager — year-end LWP forecast across all employees */}
        {['admin', 'hr_manager', 'hr_executive', 'manager'].includes(user?.role) && (
          <div className="mb-4">
            <LwpForecastCard />
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="all">All ({leaves.length})</TabsTrigger>
            <TabsTrigger value="pending">Pending ({leaves.filter(l => l.status === 'pending').length})</TabsTrigger>
            <TabsTrigger value="approved">Approved ({leaves.filter(l => l.status === 'approved').length})</TabsTrigger>
            <TabsTrigger value="rejected">Rejected ({leaves.filter(l => l.status === 'rejected').length})</TabsTrigger>
            {hasTeam && (
              <TabsTrigger value="team" className="flex items-center gap-1">
                <Users size={14} />
                Team ({teamPendingLeaves.length} pending)
              </TabsTrigger>
            )}
          </TabsList>

          {/* Team Leaves Tab Content */}
          {hasTeam && activeTab === 'team' && (
            <TabsContent value="team" className="mt-4">
              {/* Team Summary Cards */}
              {teamSummary && (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                  <Card className="bg-indigo-50 border-indigo-200">
                    <CardContent className="p-4">
                      <p className="text-sm text-indigo-600">Team Size</p>
                      <p className="text-2xl font-bold text-indigo-800">{teamSummary.team_size || 0}</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-amber-50 border-amber-200">
                    <CardContent className="p-4">
                      <p className="text-sm text-amber-600">Pending Approval</p>
                      <p className="text-2xl font-bold text-amber-800">{teamSummary.pending_count || 0}</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-emerald-50 border-emerald-200">
                    <CardContent className="p-4">
                      <p className="text-sm text-emerald-600">Approved</p>
                      <p className="text-2xl font-bold text-emerald-800">{teamSummary.summary?.approved || 0}</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-rose-50 border-rose-200">
                    <CardContent className="p-4">
                      <p className="text-sm text-rose-600">Rejected</p>
                      <p className="text-2xl font-bold text-rose-800">{teamSummary.summary?.rejected || 0}</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-blue-50 border-blue-200">
                    <CardContent className="p-4">
                      <p className="text-sm text-blue-600">Total Days Taken</p>
                      <p className="text-2xl font-bold text-blue-800">{teamSummary.summary?.total_days_taken || 0}</p>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Team Members Summary with Leave Balances */}
              {teamBalances?.balances && teamBalances.balances.length > 0 ? (
                <Card className="mb-6">
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Users className="text-indigo-600" size={18} />
                      Team Leave Balances ({teamBalances.year})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm" data-testid="team-balance-table">
                        <thead>
                          <tr className="border-b border-slate-200 text-left">
                            <th className="py-2.5 px-3 font-semibold text-slate-700">Employee</th>
                            <th className="text-center py-2.5 px-2 font-semibold text-blue-700 text-xs">Sick</th>
                            <th className="text-center py-2.5 px-2 font-semibold text-indigo-700 text-xs">Casual</th>
                            <th className="text-center py-2.5 px-2 font-semibold text-purple-700 text-xs">Annual</th>
                            <th className="text-center py-2.5 px-2 font-semibold text-slate-600 text-xs">Unpaid</th>
                            <th className="text-center py-2.5 px-3 font-semibold text-emerald-700 text-xs">Remaining</th>
                          </tr>
                        </thead>
                        <tbody>
                          {teamBalances.balances.map((mb, idx) => (
                            <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`team-balance-row-${idx}`}>
                              <td className="py-2.5 px-3">
                                <div className="flex items-center gap-2">
                                  <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center text-xs font-bold text-indigo-700">
                                    {mb.name?.split(' ').map(n => n[0]).join('').substring(0, 2)}
                                  </div>
                                  <div>
                                    <p className="font-medium text-slate-800 text-sm">{mb.name}</p>
                                    <p className="text-xs text-slate-400">{mb.designation || mb.employee_code}</p>
                                  </div>
                                </div>
                              </td>
                              <td className="text-center py-2.5 px-2">
                                <span className="text-xs">{mb.balance.sick.used}/{mb.balance.sick.quota}</span>
                              </td>
                              <td className="text-center py-2.5 px-2">
                                <span className="text-xs">{mb.balance.casual.used}/{mb.balance.casual.quota}</span>
                              </td>
                              <td className="text-center py-2.5 px-2">
                                <span className="text-xs">{mb.balance.annual.used}/{mb.balance.annual.quota}</span>
                              </td>
                              <td className="text-center py-2.5 px-2">
                                <span className="text-xs text-slate-500">{mb.balance.unpaid.used}</span>
                              </td>
                              <td className="text-center py-2.5 px-3">
                                <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                                  mb.total_remaining <= 2 ? 'bg-red-100 text-red-700' :
                                  mb.total_remaining <= 5 ? 'bg-amber-100 text-amber-700' :
                                  'bg-emerald-100 text-emerald-700'
                                }`}>
                                  {mb.total_remaining}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              ) : teamSummary?.team_members && teamSummary.team_members.length > 0 ? (
                <Card className="mb-6">
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Users className="text-indigo-600" size={18} />
                      Team Leave Summary
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {teamSummary.team_members.map(member => (
                        <div key={member.id} className="p-4 border rounded-lg hover:bg-gray-50">
                          <div className="flex items-center gap-3 mb-2">
                            <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
                              <User className="text-indigo-600" size={18} />
                            </div>
                            <div>
                              <p className="font-medium text-sm">{member.name}</p>
                              <p className="text-xs text-gray-500">{member.designation}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-4 text-sm">
                            <span className="text-emerald-600">{member.approved_days} days taken</span>
                            {member.pending > 0 && (
                              <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                                {member.pending} pending
                              </Badge>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ) : null}

              {/* Pending Team Leaves requiring approval */}
              {teamPendingLeaves.length > 0 && (
                <Card className="mb-6 border-amber-200">
                  <CardHeader className="bg-amber-50">
                    <CardTitle className="text-base flex items-center gap-2 text-amber-800">
                      <AlertCircle size={18} />
                      Pending Your Approval ({teamPendingLeaves.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-4">
                    <div className="space-y-3">
                      {teamPendingLeaves.map(leave => (
                        <div key={leave.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50">
                          <div className="flex items-center gap-4">
                            <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                              <User className="text-amber-600" size={18} />
                            </div>
                            <div>
                              <p className="font-medium">{leave.employee_name}</p>
                              <p className="text-sm text-gray-500">
                                {leave.leave_type?.replace('_', ' ')} • {leave.start_date} to {leave.end_date}
                              </p>
                              <p className="text-xs text-gray-400">{leave.reason}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge>{leave.total_days || 1} day(s)</Badge>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                              onClick={() => {
                                setSelectedLeave(leave);
                                setWorkflowDialogOpen(true);
                              }}
                            >
                              <Check size={14} className="mr-1" /> Review
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* View Toggle and Team Leaves */}
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-base">Team Leaves</CardTitle>
                  <div className="flex items-center gap-2">
                    <Button
                      variant={teamViewMode === 'list' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setTeamViewMode('list')}
                    >
                      <LayoutList size={14} className="mr-1" /> List
                    </Button>
                    <Button
                      variant={teamViewMode === 'calendar' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setTeamViewMode('calendar')}
                    >
                      <CalendarDays size={14} className="mr-1" /> Calendar
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {teamViewMode === 'calendar' ? (
                    <TeamLeaveCalendar 
                      teamLeaves={teamLeaves} 
                      calendarMonth={calendarMonth}
                      setCalendarMonth={setCalendarMonth}
                      conflictThreshold={conflictThreshold}
                    />
                  ) : (
                    <>
                      {teamLeaves.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                          <Calendar className="h-12 w-12 mx-auto mb-4 opacity-50" />
                          <p>No team leave requests found</p>
                        </div>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead>
                              <tr className="border-b text-left">
                                <th className="pb-3 font-medium">Employee</th>
                                <th className="pb-3 font-medium">Type</th>
                                <th className="pb-3 font-medium">Dates</th>
                                <th className="pb-3 font-medium">Days</th>
                                <th className="pb-3 font-medium">Status</th>
                                <th className="pb-3 font-medium">Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {teamLeaves.map(leave => (
                                <tr key={leave.id} className="border-b hover:bg-gray-50">
                                  <td className="py-3">
                                    <div className="flex items-center gap-2">
                                      <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center">
                                        <User className="text-indigo-600" size={14} />
                                      </div>
                                      <div>
                                        <p className="font-medium text-sm">{leave.employee_name}</p>
                                        <p className="text-xs text-gray-500">{leave.employee_code}</p>
                                      </div>
                                    </div>
                                  </td>
                                  <td className="py-3">
                                    <Badge variant="outline" className="capitalize">
                                      {leave.leave_type?.replace('_', ' ')}
                                    </Badge>
                                  </td>
                                  <td className="py-3 text-sm">
                                    {leave.start_date} to {leave.end_date}
                                  </td>
                                  <td className="py-3">{leave.total_days || 1}</td>
                                  <td className="py-3">
                                    <Badge className={
                                      leave.status === 'approved' ? 'bg-emerald-100 text-emerald-800' :
                                      leave.status === 'pending' ? 'bg-amber-100 text-amber-800' :
                                      'bg-rose-100 text-rose-800'
                                    }>
                                      {leave.status}
                                    </Badge>
                                  </td>
                                  <td className="py-3">
                                    {leave.status === 'pending' && leave.current_approval_level === 'manager' && (
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => {
                                          setSelectedLeave(leave);
                                          setWorkflowDialogOpen(true);
                                        }}
                                      >
                                        <Eye size={14} className="mr-1" /> Review
                                      </Button>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {activeTab !== 'team' && (
          <TabsContent value={activeTab} className="mt-4">
            <Card>
              <CardContent className="pt-6">
                {filteredLeaves.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Calendar className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No leave requests found</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b text-left">
                          <th className="pb-3 font-medium">Employee</th>
                          <th className="pb-3 font-medium">Type</th>
                          <th className="pb-3 font-medium">Dates</th>
                          <th className="pb-3 font-medium">Days</th>
                          <th className="pb-3 font-medium">Status</th>
                          <th className="pb-3 font-medium">Current Level</th>
                          <th className="pb-3 font-medium">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredLeaves.map(leave => (
                          <tr key={leave.id} className="border-b hover:bg-gray-50">
                            <td className="py-4">
                              <div>
                                <p className="font-medium">{leave.employee_name || getEmployeeName(leave.employee_id)}</p>
                                <p className="text-sm text-gray-500">{leave.department}</p>
                              </div>
                            </td>
                            <td className="py-4">
                              <Badge variant="outline" className="capitalize">{leave.leave_type}</Badge>
                            </td>
                            <td className="py-4">
                              <p className="text-sm">{leave.start_date}</p>
                              <p className="text-sm text-gray-500">to {leave.end_date}</p>
                            </td>
                            <td className="py-4">
                              <span className="font-semibold">{leave.total_days || 1}</span>
                              {leave.is_half_day && <span className="text-xs text-gray-500 ml-1">(Half)</span>}
                            </td>
                            <td className="py-4">{getStatusBadge(leave.status)}</td>
                            <td className="py-4">
                              {leave.status === 'pending' && getApprovalLevelBadge(leave.current_approval_level)}
                            </td>
                            <td className="py-4">
                              <div className="flex gap-2">
                                <Button size="sm" variant="ghost" onClick={() => viewWorkflow(leave)} title="View Details">
                                  <Eye className="h-4 w-4" />
                                </Button>
                                {/* Admin can modify approved leaves */}
                                {user?.role === 'admin' && leave.status === 'approved' && (
                                  <Button 
                                    size="sm" 
                                    variant="ghost" 
                                    className="text-amber-600"
                                    onClick={() => handleModifyClick(leave)}
                                    title="Modify Leave"
                                  >
                                    <Edit className="h-4 w-4" />
                                  </Button>
                                )}
                                {/* Admin can delete any leave */}
                                {user?.role === 'admin' && (
                                  <Button 
                                    size="sm" 
                                    variant="ghost" 
                                    className="text-red-600"
                                    onClick={() => handleDeleteClick(leave)}
                                    title="Delete Leave"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                )}
                                {/* Show modification history indicator */}
                                {leave.modification_history?.length > 0 && (
                                  <Badge variant="outline" className="text-xs bg-amber-50 text-amber-600 border-amber-200">
                                    <History className="h-3 w-3 mr-1" />
                                    Modified
                                  </Badge>
                                )}
                                {/* Backdated indicator */}
                                {leave.is_backdated && (
                                  <Badge variant="outline" className="text-xs bg-purple-50 text-purple-600 border-purple-200">
                                    Backdated
                                  </Badge>
                                )}
                                {canApprove(leave) && (
                                  <>
                                    <Button size="sm" variant="ghost" className="text-green-600" onClick={() => {
                                      setSelectedLeave(leave);
                                      setWorkflowDialogOpen(true);
                                    }}>
                                      <Check className="h-4 w-4" />
                                    </Button>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          )}
        </Tabs>

        {/* Workflow Dialog */}
        <Dialog open={workflowDialogOpen} onOpenChange={setWorkflowDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Leave Approval Workflow</DialogTitle>
            </DialogHeader>
            {selectedLeave && (
              <div className="space-y-6">
                {/* Leave Details */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-500">Employee</p>
                      <p className="font-medium">{selectedLeave.employee_name || getEmployeeName(selectedLeave.employee_id)}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Leave Type</p>
                      <p className="font-medium capitalize">{selectedLeave.leave_type}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Duration</p>
                      <p className="font-medium">{selectedLeave.start_date} to {selectedLeave.end_date}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Total Days</p>
                      <p className="font-medium">{selectedLeave.total_days || 1}</p>
                    </div>
                  </div>
                  <div className="mt-3">
                    <p className="text-gray-500 text-sm">Reason</p>
                    <p className="text-sm">{selectedLeave.reason}</p>
                  </div>
                </div>

                {/* Workflow Steps */}
                <div>
                  <h4 className="font-medium mb-3">Approval Workflow</h4>
                  <div className="space-y-3">
                    {(selectedLeave.approval_workflow || []).map((step, index) => (
                      <div key={index} className="flex items-center gap-3 p-3 border rounded-lg">
                        {getWorkflowStepIcon(step.status)}
                        <div className="flex-1">
                          <p className="font-medium capitalize">{step.level} Approval</p>
                          {step.approver_name && (
                            <p className="text-sm text-gray-500">
                              {step.status === 'approved' ? 'Approved by' : step.status === 'rejected' ? 'Rejected by' : 'Pending'}: {step.approver_name}
                            </p>
                          )}
                          {step.remarks && <p className="text-sm text-gray-400">&ldquo;{step.remarks}&rdquo;</p>}
                        </div>
                        <Badge variant={step.status === 'approved' ? 'default' : step.status === 'rejected' ? 'destructive' : 'secondary'}>
                          {step.status}
                        </Badge>
                      </div>
                    ))}
                    {(!selectedLeave.approval_workflow || selectedLeave.approval_workflow.length === 0) && (
                      <p className="text-gray-500 text-sm">No workflow data available (legacy leave)</p>
                    )}
                  </div>
                </div>

                {/* Approval Actions */}
                {canApprove(selectedLeave) && (
                  <div className="border-t pt-4">
                    <Label>Remarks (Optional)</Label>
                    <Textarea
                      value={approvalRemarks}
                      onChange={(e) => setApprovalRemarks(e.target.value)}
                      placeholder="Add any remarks..."
                      className="mt-2"
                    />
                    <div className="flex gap-3 mt-4">
                      <Button 
                        className="flex-1 bg-green-600 hover:bg-green-700"
                        onClick={() => handleApproval(selectedLeave.id, 'approved')}
                      >
                        <Check className="h-4 w-4 mr-2" />
                        Approve
                      </Button>
                      <Button 
                        variant="destructive"
                        className="flex-1"
                        onClick={() => handleApproval(selectedLeave.id, 'rejected')}
                      >
                        <X className="h-4 w-4 mr-2" />
                        Reject
                      </Button>
                    </div>
                  </div>
                )}

                {/* Rejection Reason */}
                {selectedLeave.rejection_reason && (
                  <div className="bg-red-50 p-3 rounded-lg border border-red-200">
                    <p className="text-sm font-medium text-red-800">Rejection Reason</p>
                    <p className="text-sm text-red-600">{selectedLeave.rejection_reason}</p>
                  </div>
                )}

                {/* Modification History */}
                {selectedLeave.modification_history?.length > 0 && (
                  <div className="bg-amber-50 p-3 rounded-lg border border-amber-200">
                    <p className="text-sm font-medium text-amber-800 mb-2">Modification History</p>
                    {selectedLeave.modification_history.map((mod, idx) => (
                      <div key={idx} className="text-sm text-amber-700 mb-1">
                        <p className="font-medium">{mod.modified_by} - {new Date(mod.modified_at).toLocaleDateString()}</p>
                        <p className="text-xs">{mod.reason}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Modify Leave Dialog - Admin Only */}
        <Dialog open={modifyDialogOpen} onOpenChange={setModifyDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="text-amber-600 flex items-center gap-2">
                <Edit className="h-5 w-5" />
                Modify Approved Leave
              </DialogTitle>
              <DialogDescription>
                Modify leave details for {leaveToModify?.employee_name}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Start Date</Label>
                  <Input
                    type="date"
                    value={modifyData.start_date}
                    onChange={(e) => setModifyData({ ...modifyData, start_date: e.target.value })}
                  />
                </div>
                <div>
                  <Label>End Date</Label>
                  <Input
                    type="date"
                    value={modifyData.end_date}
                    onChange={(e) => setModifyData({ ...modifyData, end_date: e.target.value })}
                  />
                </div>
              </div>
              <div>
                <Label>Leave Type</Label>
                <Select
                  value={modifyData.leave_type}
                  onValueChange={(value) => setModifyData({ ...modifyData, leave_type: value })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sick">Sick Leave</SelectItem>
                    <SelectItem value="casual">Casual Leave</SelectItem>
                    <SelectItem value="annual">Annual Leave</SelectItem>
                    <SelectItem value="unpaid">Unpaid Leave</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Reason for Leave</Label>
                <Textarea
                  value={modifyData.reason}
                  onChange={(e) => setModifyData({ ...modifyData, reason: e.target.value })}
                  placeholder="Leave reason..."
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="modify-half-day"
                  checked={modifyData.is_half_day}
                  onChange={(e) => setModifyData({ ...modifyData, is_half_day: e.target.checked })}
                  className="rounded"
                />
                <Label htmlFor="modify-half-day">Half Day</Label>
              </div>
              <div>
                <Label className="text-red-600">Modification Reason *</Label>
                <Textarea
                  value={modifyData.modification_reason}
                  onChange={(e) => setModifyData({ ...modifyData, modification_reason: e.target.value })}
                  placeholder="Why are you modifying this leave? (Required)"
                  required
                />
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setModifyDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleModifySubmit} className="bg-amber-600 hover:bg-amber-700">
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Backdated Leave Dialog - HR Manager & Admin */}
        <Dialog open={backdatedDialogOpen} onOpenChange={setBackdatedDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="text-purple-600 flex items-center gap-2">
                <CalendarPlus className="h-5 w-5" />
                Apply Backdated Leave
              </DialogTitle>
              <DialogDescription>
                Record leave that was taken in the past but not entered in the system
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Employee *</Label>
                <Input
                  type="text"
                  placeholder="Search employee..."
                  value={employeeSearchQuery}
                  onChange={(e) => setEmployeeSearchQuery(e.target.value)}
                  className="mb-2"
                />
                <Select
                  value={backdatedData.employee_id}
                  onValueChange={(value) => setBackdatedData({ ...backdatedData, employee_id: value })}
                >
                  <SelectTrigger><SelectValue placeholder="Select employee" /></SelectTrigger>
                  <SelectContent>
                    {filteredEmployees.slice(0, 50).map(emp => (
                      <SelectItem key={emp.id} value={emp.id}>
                        {emp.name || emp.employee_id} - {emp.employee_id}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Leave Type *</Label>
                <Select
                  value={backdatedData.leave_type}
                  onValueChange={(value) => setBackdatedData({ ...backdatedData, leave_type: value })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sick">Sick Leave</SelectItem>
                    <SelectItem value="casual">Casual Leave</SelectItem>
                    <SelectItem value="annual">Annual Leave</SelectItem>
                    <SelectItem value="unpaid">Unpaid Leave</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Start Date * (Past only)</Label>
                  <Input
                    type="date"
                    value={backdatedData.start_date}
                    onChange={(e) => setBackdatedData({ ...backdatedData, start_date: e.target.value })}
                    max={getYesterdayDate()}
                    required
                  />
                </div>
                <div>
                  <Label>End Date * (Past only)</Label>
                  <Input
                    type="date"
                    value={backdatedData.end_date}
                    onChange={(e) => setBackdatedData({ ...backdatedData, end_date: e.target.value })}
                    max={getYesterdayDate()}
                    required
                  />
                </div>
              </div>
              <div>
                <Label>Reason *</Label>
                <Textarea
                  value={backdatedData.reason}
                  onChange={(e) => setBackdatedData({ ...backdatedData, reason: e.target.value })}
                  placeholder="Reason for backdated leave entry..."
                  required
                />
              </div>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={backdatedData.is_half_day}
                    onChange={(e) => setBackdatedData({ ...backdatedData, is_half_day: e.target.checked })}
                    className="rounded"
                  />
                  <span className="text-sm">Half Day</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={backdatedData.auto_approve}
                    onChange={(e) => setBackdatedData({ ...backdatedData, auto_approve: e.target.checked })}
                    className="rounded"
                  />
                  <span className="text-sm">Auto-approve</span>
                </label>
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setBackdatedDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleBackdatedSubmit} className="bg-purple-600 hover:bg-purple-700">
                Create Backdated Leave
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Leave Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="text-red-600 flex items-center gap-2">
                <Trash2 className="h-5 w-5" />
                Delete Leave
              </DialogTitle>
              <DialogDescription>
                Are you sure you want to delete this leave record? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            {leaveToDelete && (
              <div className="p-4 bg-slate-50 rounded-lg space-y-2">
                <p><span className="font-medium">Employee:</span> {leaveToDelete.employee_name}</p>
                <p><span className="font-medium">Type:</span> {leaveToDelete.leave_type}</p>
                <p><span className="font-medium">Dates:</span> {leaveToDelete.start_date} to {leaveToDelete.end_date}</p>
                <p><span className="font-medium">Status:</span> <Badge className={leaveToDelete.status === 'approved' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>{leaveToDelete.status}</Badge></p>
              </div>
            )}
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
              <Button variant="destructive" onClick={handleDeleteConfirm} className="bg-red-600 hover:bg-red-700">
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Leave
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Audit Log Dialog - Admin Only */}
        <Dialog open={auditLogDialogOpen} onOpenChange={setAuditLogDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-slate-700 flex items-center gap-2">
                <ScrollText className="h-5 w-5" />
                Leave Audit Log
              </DialogTitle>
              <DialogDescription>
                Complete history of all leave activities (create, approve, reject, modify, delete)
              </DialogDescription>
            </DialogHeader>
            
            {loadingLogs ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
              </div>
            ) : auditLogs.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                No audit logs found
              </div>
            ) : (
              <div className="space-y-3">
                {auditLogs.map((log, index) => (
                  <div key={log.id || index} className="p-3 border rounded-lg bg-white hover:bg-slate-50">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        {getActionBadge(log.action)}
                        <div>
                          <p className="font-medium text-slate-900">{log.employee_name}</p>
                          <p className="text-xs text-slate-500">
                            by {log.action_by_name} ({log.action_by_role})
                          </p>
                        </div>
                      </div>
                      <p className="text-xs text-slate-400">
                        {new Date(log.timestamp).toLocaleString()}
                      </p>
                    </div>
                    {log.details && Object.keys(log.details).length > 0 && (
                      <div className="mt-2 pt-2 border-t text-sm text-slate-600">
                        {log.details.leave_type && (
                          <span className="mr-3">Type: {log.details.leave_type}</span>
                        )}
                        {log.details.dates && (
                          <span className="mr-3">Dates: {log.details.dates}</span>
                        )}
                        {log.details.total_days && (
                          <span className="mr-3">Days: {log.details.total_days}</span>
                        )}
                        {log.details.level && (
                          <span className="mr-3">Level: {log.details.level}</span>
                        )}
                        {log.details.remarks && (
                          <span className="mr-3">Remarks: {log.details.remarks}</span>
                        )}
                        {log.details.modification_reason && (
                          <span className="mr-3">Reason: {log.details.modification_reason}</span>
                        )}
                        {log.details.leave_status && (
                          <span className="mr-3">Was: {log.details.leave_status}</span>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setAuditLogDialogOpen(false)}>Close</Button>
              <Button onClick={fetchAuditLogs} className="bg-indigo-600 hover:bg-indigo-700">
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <LeaveSplitConfirmDialog
          open={splitDialogOpen}
          onOpenChange={(open) => { setSplitDialogOpen(open); if (!open) setSplitPreview(null); }}
          preview={splitPreview}
          onConfirm={handleConfirmSplit}
          busy={splitBusy}
        />
      </div>
    </Layout>
  );
};

export default LeaveManagement;
